import { useStore } from 'zustand';
import { useMeasuresStore } from '@/hooks/store/use-measure-store';
import { useDesignStore } from '@/hooks/store/use-design-store';
import { useMeasureOptionsStore } from '@/hooks/store/use-measure-options-store';
import { usePianoProfileState, usePianoProfileStore } from '@/hooks/store/use-piano-profile-store';
import { buildFullBackupExportPayload, createMeasuresExportBlob } from '@/lib/backup/measure-backup';
import { useCallback, useState } from 'react';
import { useDownloadBlob } from './use-download-blob';
import { formatISO } from 'date-fns';
import { useShallow } from 'zustand/shallow';
import slug from 'slug';
export const useExportBackupFile = () => {
  const measureStoreState = useStore(useMeasuresStore(), useShallow(state => ({
    keyWeightRatio: state.keyWeightRatio,
    keys: state.keys,
    wippenRadiusWeight: state.wippenRadiusWeight
  })));
  const designStoreState = useStore(useDesignStore(), useShallow(state_0 => ({
    frontWeightDesignComputedBalanceWeightTarget: state_0.frontWeightDesignComputedBalanceWeightTarget,
    frontWeightDesignMode: state_0.frontWeightDesignMode,
    frontWeightDesignStandardTarget: state_0.frontWeightDesignStandardTarget,
    frontWeightDesignTarget: state_0.frontWeightDesignTarget,
    strikeWeightDesignComputedBalanceWeightTarget: state_0.strikeWeightDesignComputedBalanceWeightTarget,
    strikeWeightDesignLatestSmoothTarget: state_0.strikeWeightDesignLatestSmoothTarget,
    strikeWeightDesignLatestStandarTarget: state_0.strikeWeightDesignLatestStandarTarget,
    strikeWeightDesignMode: state_0.strikeWeightDesignMode,
    strikeWeightDesignTarget: state_0.strikeWeightDesignTarget,
    strikeWeightRatioDesignLatestFixedTarget: state_0.strikeWeightRatioDesignLatestFixedTarget,
    strikeWeightRatioDesignLatestSmoothTarget: state_0.strikeWeightRatioDesignLatestSmoothTarget,
    strikeWeightRatioDesignMode: state_0.strikeWeightRatioDesignMode,
    strikeWeightRatioDesignTarget: state_0.strikeWeightRatioDesignTarget,
    wippenSupportSpringsDesignLatestNumberOfSprings: state_0.wippenSupportSpringsDesignLatestNumberOfSprings,
    wippenSupportSpringsDesignLatestSpringBalanceWeight: state_0.wippenSupportSpringsDesignLatestSpringBalanceWeight,
    wippenSupportSpringsDesignLatestTensionDropIndex: state_0.wippenSupportSpringsDesignLatestTensionDropIndex,
    wippenSupportSpringsDesignMode: state_0.wippenSupportSpringsDesignMode,
    wippenSupportSpringsDesignTarget: state_0.wippenSupportSpringsDesignTarget
  })));
  const measureOptionsState = useStore(useMeasureOptionsStore(), useShallow(state_1 => ({
    useManualSWRMeasurements: state_1.useManualSWRMeasurements,
    useSupportSpringMeasurements: state_1.useSupportSpringMeasurements
  })));
  const pianoProfileState = useStore(usePianoProfileStore(), useShallow(state_2 => ({
    brand: state_2.brand,
    keyCount: state_2.keyCount,
    model: state_2.model,
    serialNumber: state_2.serialNumber,
    startNote: state_2.startNote
  })));
  const [isExporting, setIsExporting] = useState(false);
  const {
    downloadBlob
  } = useDownloadBlob();
  const {
    displayName
  } = usePianoProfileState();
  const exportBackupFile = useCallback(() => {
    const currentDay = formatISO(new Date(), {
      representation: 'date'
    });

    // `||`: a profile with no make or model has an empty name, not a missing
    // one, and was saved as "-2026-10-12.json".
    const slugifiedPianoName = slug(displayName || 'my-piano');
    const filename = `${slugifiedPianoName}-${currentDay}.json`;
    setIsExporting(true);
    try {
      const payload = buildFullBackupExportPayload({
        design: designStoreState,
        measureOptions: measureOptionsState,
        measures: {
          keyWeightRatio: measureStoreState.keyWeightRatio,
          keys: measureStoreState.keys.slice(0, pianoProfileState.keyCount),
          wippenRadiusWeight: measureStoreState.wippenRadiusWeight
        },
        piano: pianoProfileState
      });
      const blob = createMeasuresExportBlob(payload);
      downloadBlob(blob, filename);
    } finally {
      setIsExporting(false);
    }
  }, [displayName, designStoreState, measureOptionsState, measureStoreState.keyWeightRatio, measureStoreState.keys, measureStoreState.wippenRadiusWeight, pianoProfileState, downloadBlob]);
  return {
    exportBackupFile,
    isExporting
  };
};